/* eslint-disable jsx-a11y/anchor-is-valid */

import { faCircleChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { postApiNews } from "../../api";
import { NewsResponseViewModel } from "../../api/models/newsResponseViewModel";
import { ReactComponent as Bookmark } from "../../assets/images/bookmark-regular.svg";
import { ReactComponent as LinkSolid } from "../../assets/images/link-solid.svg";
import market from "../../assets/images/market.svg";
import country from "../../assets/images/paises.svg";
import figures from "../../assets/images/publicaciones.svg";
import product from "../../assets/images/shoping.svg";
import todo from "../../assets/images/todo.svg";
import { News } from "../../models/news.model";
import CircularLoading from "../../shared/CircularLoading/CircularLoading";
import CopyLinkButton from "../../shared/CopyLinkButton/CopyLinkButton";
import { FriendlyDate } from "../FriendlyDate/FriendlyDate";
import "./Main.css";


const Main: React.FC = () => {
  const {t, i18n} = useTranslation()
  const preferencesSelected = window.localStorage.getItem(
    "preferencesSelected"
  );
  const [activeTab, setActiveTab] = useState<number>(0);
  const [content, setContent] = useState<News[]>([]);
  const [displayedItems, setDisplayedItems] = useState<number>(3); // Initially show 3 items
  const [allContent, setAllContent] = useState<News[]>([]); // Store all filtered content
  const [newsData, setNewsData] = useState<News[]>([]); // New state for API news data
  const [isLoading, setIsLoading] = useState(false);
  
  const [tagSelected, setTagSelected] = useState<string>("");

  const tabs = [
    { id: 0, title: t('main.all'), image: todo },
    { id: 1, title: t('main.markets'), image: market },
    { id: 2, title: t('main.products'), image: product },
    { id: 3, title: t('main.countries'), image: country },
    { id: 4, title: t('main.publications'), image: figures },
    { id: 5, title: t('main.other'), image: market },
  ];

  const tabSectionMapping: { [key: number]: string } = {
    0: '', // All - no filter
    1: 'mercados',
    2: 'productos',
    3: 'financieras',
    4: 'publicaciones',
    5: 'otros'
  };

  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    const fetchNews = async () => {
      try {
        const response = await postApiNews({
          page: 1,
          size: 100
        });

        // Map the API response to our News type
        const mappedNews: any[] = (response.results || []).map((item: NewsResponseViewModel) => ({
          id: item.id || 0,
          image: item.image || '',
          title: item.title || '',
          summary: item.summary || '',
          date: item.date || '',
          source: item.source || '',
          link: item.link || undefined,
          body: item.body || undefined,
          preferences: item.preferences || '', // Store as string instead of array
        }));
        setNewsData(mappedNews);
      } catch (error) {
        console.error('Error fetching news:', error);
        setNewsData([]); // Set empty array on error
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNews();
  }, [i18n.language]);

  useEffect(() => {
    const newContent = obtenerContenidoSegunTab(activeTab);
    setAllContent(newContent);
    setDisplayedItems(3); // Reset to initial display count when tab changes
    setContent(newContent.slice(0, 3)); // Show only first 3 items
  },[newsData, activeTab])

  const handleButtonClick = (url: string) => {
    navigate(url);
  };

  const obtenerContenidoSegunTab = (index: number): News[] => {
    return newsData; // Simply return the current newsData which is already filtered by the API
  };

  const handleTabClick = async (index: number) => {
    setActiveTab(index);
    setIsLoading(true);

    setNewsData([]);
    setAllContent([]);
    setContent([]);
    
    try {
      const response = await postApiNews({
        page: 1,
        size: 100,
        filter: {
          section: tabSectionMapping[index]
        }
      });

      const mappedNews: News[] = (response.results || []).map((item: NewsResponseViewModel) => ({
        id: item.id || '',
        image: item.image || '',
        title: item.title || '',
        summary: item.summary || '',
        date: item.date || '',
        source: item.source || '',
        link: item.link || undefined,
        body: item.body || undefined,
        preferences: item.preferences || '',
      }));

      setNewsData(mappedNews);
      setAllContent(mappedNews);
      setDisplayedItems(3);
      setContent(mappedNews.slice(0, 3));
    } catch (error) {
      console.error('Error fetching news for tab:', error);
      setNewsData([]);
      setAllContent([]);
      setContent([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextTabClick = async (index: number) => {
    let nextTab = index + 1;
    if (nextTab >= tabs.length) {
      nextTab = 0;
    }
    setActiveTab(nextTab);
    setIsLoading(true);

    setNewsData([]);
    setAllContent([]);
    setContent([]);
    
    try {
      const response = await postApiNews({
        page: 1,
        size: 100,
        filter: {
          section: tabSectionMapping[nextTab]
        }
      });

      const mappedNews: News[] = (response.results || []).map((item: NewsResponseViewModel) => ({
        id: item.id || '',
        image: item.image || '',
        title: item.title || '',
        summary: item.summary || '',
        date: item.date || '',
        source: item.source || '',
        link: item.link || undefined,
        body: item.body || undefined,
        preferences: item.preferences || '',
      }));

      setNewsData(mappedNews);
      setAllContent(mappedNews);
      setDisplayedItems(3);
      setContent(mappedNews.slice(0, 3));
    } catch (error) {
      console.error('Error fetching news for tab:', error);
      setNewsData([]);
      setAllContent([]);
      setContent([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onSelectTag = async (itemSelected: any) => {
    setTagSelected(itemSelected);
    let newsFiltered: any = [];
    if (newsData) { // Use newsData instead of dataNewsList
      newsData.map((item) => {
        const itemFounded = item?.preferences?.split(';').find(
          (e) => e.trim() === itemSelected.label
        );
        if (itemFounded) {
          newsFiltered.push(item);
        }
      });
    }

    setAllContent(newsFiltered);
    setDisplayedItems(3); // Reset to initial display count
    setContent(newsFiltered.slice(0, 3)); // Show only first 3 items
  };

  const loadMoreItems = () => {
    const newDisplayedItems = displayedItems + 5;
    setDisplayedItems(newDisplayedItems);
    setContent(allContent.slice(0, newDisplayedItems));
  };

  return (
    <main>
      <div className="container-main">
        <div className="navtabs">
          {tabs.map((tab, index) => (
            <span
              key={tab.id + index}
              className={`navtabs__tab ${activeTab === index ? "active" : ""}`} // Aplica la clase 'active' si la pestaña está activa
              onClick={() => handleTabClick(index)} // Maneja el clic en la pestaña
            >
              <img src={tab.image} alt={tab.title} className="navtabs__tab__icon" />
              <div className="navtabs__tab__text">{tab.title}</div>
            </span>
          ))}
          {/* <span
            className="navtabs__tab-next"
            onClick={() => handleNextTabClick(tabs[activeTab].id)}
          >
            <FontAwesomeIcon icon={faCircleChevronRight} className="btn-next" />
          </span> */}
        </div>
        <div className="tabs__body">
          {activeTab === 0 && (
            <div className="tabs__more" key={preferencesSelected ?? "default"}>
              {preferencesSelected &&
                JSON.parse(preferencesSelected).map((item: any) => (
                  <span key={item?.id}
                    className={`preference ${
                      item?.label === tagSelected ? "tagActive" : ""
                    }`}
                    onClick={() => onSelectTag(item)}
                  >
                    <p className="preference-text">{item?.tag?.label}</p>
                  </span>
                ))}
              {/* <span>
                <a href="/preferencias" className="tabs__more__text ">{t('main.see_all')}</a>
              </span> */}
            </div>
          )}
          {isLoading && 
          <div className="loading-news">
            <CircularLoading />
          </div>
          }
          {content.map((item, index) => (
            <div
              key={index + item?.id}
              
              className={`itemnews ${index === 0 ? "itemnews--big" : ""}`}
            >
              <div id="tab-content" className="itemnews__content tab-content">

                <div className="itemnews__row ">
                  <div className="itemnews__fixed col-auto d-lg-block" onClick={() => handleButtonClick(`/noticia/${item.id}`)}>
                    <img
                      className="itemnews__img"
                      src={item.image}
                      alt={item.title}
                      width={129}
                      height={129}
                     
                    />
                  </div>
                  <div className="itemnews__flex ">
                    <h3 className="itemnews__title " onClick={() => handleButtonClick(`/noticia/${item.id}`)}>{item.title}</h3>
                    <p className="itemnews__text" onClick={() => handleButtonClick(`/noticia/${item.id}`)}>{item.summary}</p>
                    <div
                      className="itemnews__footer"
                    >
                      <div className="itemnews__footer__col">
                        <span className="itemnews__date "><FriendlyDate date={item.date} /></span>
                        <div className="itemnews__tag ">
                          ● {item.source}
                        </div>
                      </div>
                      <div className="itemnews__footer__col">
                        <div className="itemnews__actions ">
                          
                          <CopyLinkButton className="itemnews__share" link={item.link} >
                            <LinkSolid className="itemnews__share__icon " />
                          </CopyLinkButton>
                          <Bookmark className="itemnews__save " />
                        </div>
                      </div>
                    </div>
                    <div className="itemnews__fillter ">
                      {/* {item?.preferences?.map((item) => (
                        <span className="itemnews__preference">
                          <p className="itemnews__preference__text ">{item}</p>
                        </span>
                      ))} */}
                      {item?.preferences ? (
                        <>
                          
                        {item?.preferences?.split(';').map((preference: string, index: number) => (
                          preference.trim() && (
                            <span key={index} className="itemnews__preference">
                              
                              <p className="itemnews__preference__text ">{preference.trim()}</p>
                            </span>
                          )
                        ))}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Load More Button */}
          {allContent.length > displayedItems && (
            <div className="load-more-container" >
              <button 
                className="load-more-button" 
                onClick={loadMoreItems}
                
              >
                {t('main.load_more') }
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default Main;
